import { Component, OnInit } from '@angular/core';
import { StoreService } from 'src/app/store.service';
import { FormBuilder, FormGroup, NgForm } from '@angular/forms';
import { NavigationExtras, ActivatedRoute, Router } from '@angular/router';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

import { JSONForm } from 'src/library/jsonform.js';
import { ThrowStmt } from '@angular/compiler';

import schemaMockdata from 'src/assets/mock-schema.json';
import { UtilitiesService } from 'src/app/core/utilities.service';
import { environment } from 'src/environments/environment';
import { Platform } from '@angular/cdk/platform';
import { DataService } from 'src/app/data.service';
import { StoreInfoService } from 'src/app/store-info.service';

declare const $: any;
declare const getColorHexCode: any;
//declare const JSONForm: any;

// Store Logo was moved to Preferences > Appearance > Store Logo. The schema
// still ships these keys — it lives in the theme preset row in the DB and is
// shared by every theme — so they are filtered out of the rendered form here
// instead of migrating each preset. The saved values are untouched: the
// storefront still reads them from custom content, and formatContentData()
// carries them through so publishing from here can't blank them.
const STORE_LOGO_KEYS = [
  'store_logo_url',
  'store_logo_width',
  'store_logo_height',
];

@Component({
  selector: 'app-content-editor',
  templateUrl: './content-editor.component.html',
  styleUrls: ['./content-editor.component.scss'],
})
export class ContentEditorComponent implements OnInit {
  token;
  store_id;
  contentData;
  contentSchema;
  lastSavedContentData;
  currentContentData;
  selectedLanguage: any;
  languages;
  sidePanelIsReady: boolean = false;
  contentDataReady: boolean = false;

  lastImageURL = '';
  lastMediaID = '';
  lastImageInputID = '';

  coordinate = { lat: 3.059789, long: 101.710968 };
  coordinateStr = '';
  lastCoordinateInputID = '';

  loadedProductPreset;
  country;
  filteredCountry;

  theme_id;
  store_url;
  safe_store_url;

  fullpageMode = true;
  closeResult = '';
  success = 'success';
  hideCtaDisplay = true;
  product_group = '';
  public productArrangement = []; // Arrangement state for current listing
  public customArrangement = {}; // Arrangement from backend, multi country
  customArrangementReady = false;
  dashboardLanguage;
  presetContent;
  design;
  isMobileView = false;
  storeInfo$ = this.storeInfoService.getStoreInfos();

  constructor(
    private svc: StoreService,
    private router: Router,
    public utils: UtilitiesService,
    public sanitizer: DomSanitizer,
    private platform: Platform,
    public dataService: DataService,
    private storeInfoService: StoreInfoService
  ) {
    window['resizePreviewContainer'] = this.resizePreviewContainer;

    // Declare cleanID function for JSONForm Template
    window['cleanId'] = (id) => {
      return id.replace(/[^a-zA-Z0-9_-]/g, '');
    };

    window['updateLocationPreview'] = (
      lat,
      long,
      nodeId,
      triggerInputChange = false
    ) => {
      if (!lat || !long) {
        console.warn('Invalid or empty latitude/longitude');
        return;
      }
      const coorStr = [lat, long].join(',');
      let cleanId = window['cleanId'](nodeId);

      const locationInputSelector = `[data-location-input-id="${cleanId}"]`;

      const mapEmbedPreviewUrl = `https://maps.google.com/maps?hl=en&q=${coorStr}&z=15&ie=UTF8&iwloc=B&output=embed`;
      $(locationInputSelector)
        .closest('.editor-location-selector')
        .find('.location-preview')
        .attr('src', mapEmbedPreviewUrl);

      // Also update input value and trigger the change together with preview
      if (triggerInputChange) {
        $(locationInputSelector).val(coorStr);
        $(locationInputSelector).trigger('change');
      }
    };

    // Declare color to hex
    window['getColorHexCode'] = (str) => {
      let ctx = document.createElement('canvas').getContext('2d');
      ctx.fillStyle = str;

      if (ctx.fillStyle !== '#000000') {
        return ctx.fillStyle;
      }

      ctx.fillStyle = `#${str}`;
      return ctx.fillStyle;
    };
    this.isMobileView = this.platform.ANDROID || this.platform.IOS;
  }

  quickSort(obj: object) {
    return Object.keys(obj ?? {})
      .sort()
      .reduce(function (result, key) {
        result[key] = obj[key];
        return result;
      }, {});
  }

  // Drops the Store Logo fields from the theme schema before JSONForm renders
  // it. Form items come through either as a plain key ("store_logo_url") or as
  // an object ({ key: 'store_logo_url', ... }), and the nesting depth differs
  // per theme, so the whole tree is walked rather than targeting a known path.
  removeStoreLogoFields(schema) {
    if (!schema) return schema;

    const isStoreLogoNode = (node) =>
      typeof node === 'string'
        ? STORE_LOGO_KEYS.includes(node)
        : STORE_LOGO_KEYS.includes(node?.key);

    const walk = (node) => {
      if (Array.isArray(node)) {
        return node.filter((item) => !isStoreLogoNode(item)).map(walk);
      }
      if (node !== null && typeof node === 'object') {
        Object.keys(node).forEach((key) => (node[key] = walk(node[key])));
      }
      return node;
    };

    if (Array.isArray(schema.form)) schema.form = walk(schema.form);
    if (schema.schema) {
      STORE_LOGO_KEYS.forEach((key) => delete schema.schema[key]);
    }

    return schema;
  }

  tryAttachValueToSchema() {
    let { contentData, contentSchema } = this;

    console.log(`contentData`, { contentData });
    console.log(`schemaMockdata`, { contentSchema });
    this.translateSchema(contentSchema);

    if (
      typeof contentSchema?.schema !== 'undefined' &&
      typeof contentData !== 'undefined'
    ) {
      // Required data is ready
      this.sidePanelIsReady = true;

      setTimeout(() => {
        // contentSchema = contentSchema; // TEST MOCKDATA TODO DEV
        contentSchema.value = contentData;
        const formSelector = '#content-editor-form';

        try {
          JSONForm.fieldTypes['textarea']['onBeforeRender'] = function (
            data,
            node
          ) {
            const dealerInfo = window['dealerInfo'];
            console.debug({dealerInfo});
            const featurePlan = dealerInfo?.dealer_plan.feature_list;
            const dealerSubs = dealerInfo?.dealer_plan.dealer_subscription_status;
            const dealerCredits = dealerInfo?.dealer_credits.totalCredit;
            const minimumCredit = dealerInfo?.dealer_credits.minimumCredit;
            const isValid = dealerInfo?.dealer_credits.is_valid;

            let enableAIAssistantByPlan = false;

            if ((featurePlan.includes('ai-assistant') && dealerCredits >= minimumCredit && isValid === true) || dealerSubs === false) {
              enableAIAssistantByPlan = true;
            }

            // Compute the value of "myvalue" here
            console.log({ data, node });

            if (data.schema.title === 'about us homepage') {
              data.schema.aiAssistant.enable = enableAIAssistantByPlan;
            }

          };
          JSONForm.fieldTypes['textarea']['template'] = `
          <button
          class="ai-assistant-button float-right <%= (schema.aiAssistant?.enable === true ? '' : 'disabled ') %>"
        >
          <span><i class="fas fa-magic mr-2"></i>AI Assistant</span>
        </button>

          <textarea id="<%= id %>" name="<%= node.name %>"
            <%= (fieldHtmlClass ? "class=\'" + fieldHtmlClass + "\' " : "") %>
            style="height:<%= elt.height || "150px" %>;width:<%= elt.width || "100%" %>;"
             aria-label="<%= node.title ? escape(node.title) : node.name %>"
            <%= (node.disabled? " disabled" : "")%>
            <%= (node.readOnly ? " readonly=\'readonly\'" : "") %>
            <%= (node.schemaElement && node.schemaElement.minLength ? " minlength=\'" + node.schemaElement.minLength + "\'" : "") %>
            <%= (node.schemaElement && node.schemaElement.maxLength ? " maxlength=\'" + node.schemaElement.maxLength + "\'" : "") %>
            <%= (node.schemaElement && node.schemaElement.required ? " required=\'required\'" : "") %>
            <%= (node.placeholder? " placeholder=" + \'"\' + escape(node.placeholder) + \'"\' : "")%>
            ><%= value %></textarea>`;

          JSONForm.fieldTypes['textarea']['onInsert'] = function (evt, node) {
            console.log('oninsert', { evt, node });
            let evtTarget = evt.target ?? node[0].el;
            let selectBtn = $(evtTarget).find('.ai-assistant-button');

            selectBtn.off('click').on('click', (e) => {
              e.preventDefault();
              console.log('Click', { e });
              $('#ai-assistant-event-transport')
                .get(0)
                .dispatchEvent(
                  new CustomEvent('openAIAssistantModal', {
                    detail: {
                      aiType: node.schemaElement?.aiAssistant?.type,
                      aiparameters: node.schemaElement?.aiAssistant?.parameters,
                      nodeId: node.id,
                      eventData: e,
                    },
                  })
                );
            });

            let removeBtn = $(evtTarget).find('.remove-image-btn');
            removeBtn.off('click').on('click', (e) => {
              e.preventDefault();
            });
          };

          switch (this.dashboardLanguage) {
            case 'zh-TW':
              JSONForm.fieldTypes['image'] = {
                template: `
              <div class="editor-image-selector col">
              <input type="hidden" name="<%= node.name %>" value="<%= value %>" id="<%= id %>" data-media-input-id="<%= cleanId(id) %>" />
              <div class="row">
                <div class="col-12 ">
                  <div class="editor-image-selector-preview checkered-bg">
                    <img src="<%= value %>" class="" />
                  </div>
                </div>
                <div class="col-12 text-center !mt-2">
                <!--<%= value %>-->
                  <button class="btn btn-success select-image-btn m-auto"><i class="fi fi-rr-picture align-middle mr-1"></i> 選擇圖片</button>
                  <!--<button class="btn btn-outline-danger remove-image-btn w-100"><i class="fi fi-rr-trash align-middle mr-1"></i> Remove Image</button>-->
                </div>
              </div>


              </div>
              `,
                fieldtemplate: true,
                inputfield: true,
                onInsert: function (evt, node) {
                  let evtTarget = evt.target ?? node[0].el;
                  let selectBtn = $(evtTarget).find(
                    '.select-image-btn, .editor-image-selector-preview'
                  );

                  selectBtn.off('click').on('click', (e) => {
                    e.preventDefault();
                    $('#media-selector-event-transport')
                      .get(0)
                      .dispatchEvent(
                        new CustomEvent('setImageInputID', {
                          detail: { imageInputID: node.id, eventData: e },
                        })
                      );
                    $(
                      '#media-selector-embed .media-selector-container .btn-select'
                    ).click();
                  });

                  let removeBtn = $(evtTarget).find('.remove-image-btn');
                  removeBtn.off('click').on('click', (e) => {
                    e.preventDefault();
                  });
                },
              };
              // Add Product Preset
              JSONForm.fieldTypes['product_preset'] = {
                //node.schemaElement.type
                fieldtemplate: true,
                inputfield: true,
                template:
                  '<select name="<%= node.name %>" id="<%= id %>"' +
                  'class=\'form-control<%= (fieldHtmlClass ? " " + fieldHtmlClass : "") %>\'' +
                  '<%= (node.schemaElement && node.schemaElement.disabled? " disabled" : "")%>' +
                  '<%= (node.schemaElement && node.schemaElement.required ? " required=\'required\'" : "") %>' +
                  '> ' +
                  `<% _.each(product_data, function(key, val)
                    { if(key instanceof Object)
                      {
                        if (value === key.value)
                        { %> <option selected value="<%= key.value %>"><%= key.title %></option> <%
                      } else
                      { %> <option value="<%= key.value %>"><%= key.title %></option> <%
                    }}
                      else
                   { if (value === key)
                    { %> <option selected value="<%= key %>"><%= key %></option> <%
                  } else { %><option value="<%= key %>"><%= key %></option> <% }}}); %> ` +
                    '</select>',
                onBeforeRender: function (evt, node) {
                  const loadedProductPreset = (window as any)
                    ?.loadedProductPreset;
                  const productPresetData = loadedProductPreset.filter(
                    (e) => e.country_code === (window as any)?.country
                  );
                  node.productPresetData = productPresetData.filter(
                    (e) =>
                      e?.is_subscription === 0 &&
                      e?.display_status != 9 &&
                      e?.product_image != ''
                  );

                  evt.product_data = Object.values(node.productPresetData).map(
                    (product: any) => {
                      return {
                        value: product.p_id,
                        title: product.product_name,
                      };
                    }
                  );

                  if (node.productPresetData.length <= 0) {
                    this.hideCtaDisplay = false;
                  } else {
                    this.hideCtaDisplay = true;
                  }
                },
                onInsert: function (evt, node) {
                  if (this.hideCtaDisplay === false) {
                    // $(`#navtabscat-內容`).text('未找到產品');
                    $(`#navtabscat`).hide();
                    $(`.jsonform-error-show_product`).show();
                    $(
                      `.jsonform-error-dontmiss_product, .jsonform-error-product_url, .jsonform-error-dontmiss_header_text, .jsonform-error-dontmiss_section_content, .jsonform-error-button_text, .jsonform-error-dontmiss_url_link`
                    ).hide();
                  } else {
                    $(`#navtabscat`).show();
                    // $(`.tab-content`).text('');
                    $(
                      `.jsonform-error-show_product, .jsonform-error-dontmiss_product, .jsonform-error-product_url, .jsonform-error-dontmiss_header_text, .jsonform-error-dontmiss_section_content, .jsonform-error-button_text, .jsonform-error-dontmiss_url_link`
                    ).show();
                  }
                  let filterProductGroup = node.productPresetData.filter(
                    (e) =>
                      e?.product_group === contentSchema.value.product_group
                  );
                  let filterProduct = node.productPresetData.filter(
                    (e) => e?.product_group
                  );

                  let defaultProductName = '';
                  let defaultProductImage = '';
                  let defaultProductId = '';
                  let defaultSectionContent = '';

                  $('#product_group').attr(
                    'data-product-group',
                    contentSchema.value.product_group
                  );

                  if (filterProductGroup[0]) {
                    defaultProductName =
                      contentSchema.value.dontmiss_header_text;
                    defaultProductImage = contentSchema.value.product_url;
                    defaultProductId = contentSchema.value.dontmiss_product;
                    defaultSectionContent =
                      contentSchema.value.dontmiss_section_content;
                  } else {
                    defaultProductName = filterProduct[0]?.product_name;
                    defaultProductImage = filterProduct[0]?.product_image;
                    defaultProductId = filterProduct[0]?.p_id;
                    defaultSectionContent = filterProduct[0]?.product_content;
                  }

                  $('.trumbowyg-editor').text(defaultSectionContent);
                  for (let i = 1; i <= 5; i++) {
                    let formId = `#jsonform-${i}-elt-`;
                    $(`${formId}dontmiss_product`).val(defaultProductId);
                    $(`${formId}dontmiss_header_text`).val(defaultProductName);
                    $(
                      '.editor-image-selector-preview, ' +
                        formId +
                        'product_url'
                    ).attr('value', defaultProductImage);
                    $(`${formId}product_url`).val(defaultProductImage);
                    $(`${formId}dontmiss_url_link`).attr(
                      'value',
                      'product/' + defaultProductId
                    );
                    $(`${formId}dontmiss_url_link`).attr(
                      'value',
                      'product/' + defaultProductId
                    );
                    $(`${formId}product_url`).val(defaultProductImage);
                    $(`${formId}product_url`)
                      .parent()
                      .find('.editor-image-selector-preview img')
                      .attr('src', defaultProductImage);
                    $(`${formId}dontmiss_section_content`).text(
                      defaultSectionContent
                    );
                  }
                },
                onChange: function (evt, node) {
                  let selectedProduct =
                    node.productPresetData.find(
                      (product) => +product.p_id === +evt.target.value
                    ) || {};

                  for (let i = 1; i <= 5; i++) {
                    $(`#jsonform-${i}-elt-dontmiss_header_text`).val(
                      selectedProduct?.product_name
                    );
                    $(`#jsonform-${i}-elt-dontmiss_section_content`).val(
                      selectedProduct?.product_content
                    );
                    $(`#jsonform-${i}-elt-product_url`).val(
                      selectedProduct?.product_image
                    );
                    $(`#jsonform-${i}-elt-product_url`)
                      .parent()
                      .find('.editor-image-selector-preview img')
                      .attr('src', selectedProduct?.product_image);
                    $('.editor-image-selector-preview').attr(
                      'value',
                      selectedProduct?.product_image
                    );
                    $(`#jsonform-${i}-elt-dontmiss_url_link`).attr(
                      'value',
                      'product/' + selectedProduct?.p_id
                    );
                  }
                  $('#product_group').attr(
                    'data-product-group',
                    selectedProduct?.product_group
                  );
                  $('.trumbowyg-editor').text(selectedProduct?.product_content);
                  console.log('selectedProduct', selectedProduct);
                  console.log('Details: ', { evt, node, selectedProduct });
                },
                onClick: function (evt, node) {},
              };
              break;
            case 'zh-CN':
              JSONForm.fieldTypes['image'] = {
                template: `
                <div class="editor-image-selector col">
                <input type="hidden" name="<%= node.name %>" value="<%= value %>" id="<%= id %>" data-media-input-id="<%= cleanId(id) %>" />
                <div class="row">
                  <div class="col-12 ">
                    <div class="editor-image-selector-preview checkered-bg">
                      <img src="<%= value %>" class="" />
                    </div>
                  </div>
                  <div class="col-12 text-center !mt-2">
                  <!--<%= value %>-->
                    <button class="btn btn-success select-image-btn m-auto"><i class="fi fi-rr-picture align-middle mr-1"></i> 选择图片</button>
                    <!--<button class="btn btn-outline-danger remove-image-btn w-100"><i class="fi fi-rr-trash align-middle mr-1"></i> Remove Image</button>-->
                  </div>
                </div>


                </div>
                `,
                fieldtemplate: true,
                inputfield: true,
                onInsert: function (evt, node) {
                  let evtTarget = evt.target ?? node[0].el;
                  let selectBtn = $(evtTarget).find(
                    '.select-image-btn, .editor-image-selector-preview'
                  );

                  selectBtn.off('click').on('click', (e) => {
                    e.preventDefault();
                    $('#media-selector-event-transport')
                      .get(0)
                      .dispatchEvent(
                        new CustomEvent('setImageInputID', {
                          detail: { imageInputID: node.id, eventData: e },
                        })
                      );
                    $(
                      '#media-selector-embed .media-selector-container .btn-select'
                    ).click();
                  });

                  let removeBtn = $(evtTarget).find('.remove-image-btn');
                  removeBtn.off('click').on('click', (e) => {
                    e.preventDefault();
                  });
                },
              };
              // Add Product Preset
              JSONForm.fieldTypes['product_preset'] = {
                //node.schemaElement.type
                fieldtemplate: true,
                inputfield: true,
                template:
                  '<select name="<%= node.name %>" id="<%= id %>"' +
                  'class=\'form-control<%= (fieldHtmlClass ? " " + fieldHtmlClass : "") %>\'' +
                  '<%= (node.schemaElement && node.schemaElement.disabled? " disabled" : "")%>' +
                  '<%= (node.schemaElement && node.schemaElement.required ? " required=\'required\'" : "") %>' +
                  '> ' +
                  `<% _.each(product_data, function(key, val)
              { if(key instanceof Object)
                {
                  if (value === key.value)
                  { %> <option selected value="<%= key.value %>"><%= key.title %></option> <%
                } else
                { %> <option value="<%= key.value %>"><%= key.title %></option> <%
              }}
              else
               { if (value === key)
                { %> <option selected value="<%= key %>"><%= key %></option> <%
              } else { %><option value="<%= key %>"><%= key %></option> <% }}}); %> ` +
                  '</select>',
                onBeforeRender: function (evt, node) {
                  const loadedProductPreset = (window as any)
                    ?.loadedProductPreset;
                  const productPresetData = loadedProductPreset.filter(
                    (e) => e.country_code === (window as any)?.country
                  );
                  node.productPresetData = productPresetData.filter(
                    (e) =>
                      e?.is_subscription === 0 &&
                      e?.display_status != 9 &&
                      e?.product_image != ''
                  );

                  evt.product_data = Object.values(node.productPresetData).map(
                    (product: any) => {
                      return {
                        value: product.p_id,
                        title: product.product_name,
                      };
                    }
                  );

                  if (node.productPresetData.length <= 0) {
                    this.hideCtaDisplay = false;
                  } else {
                    this.hideCtaDisplay = true;
                  }
                  console.log(
                    'Selected product preset:',
                    node.productPresetData
                  );
                },
                onInsert: function (evt, node) {
                  if (this.hideCtaDisplay === false) {
                    // $(`#navtabscat-内容`).text('未找到产品');
                    $(`#navtabscat`).hide();
                    $(`.jsonform-error-show_product`).show();
                    $(
                      `.jsonform-error-dontmiss_product, .jsonform-error-product_url, .jsonform-error-dontmiss_header_text, .jsonform-error-dontmiss_section_content, .jsonform-error-button_text, .jsonform-error-dontmiss_url_link`
                    ).hide();
                  } else {
                    $(`#navtabscat`).show();
                    // $(`.tab-content`).text('');
                    $(
                      `.jsonform-error-show_product, .jsonform-error-dontmiss_product, .jsonform-error-product_url, .jsonform-error-dontmiss_header_text, .jsonform-error-dontmiss_section_content, .jsonform-error-button_text, .jsonform-error-dontmiss_url_link`
                    ).show();
                  }
                  let filterProductGroup = node.productPresetData.filter(
                    (e) =>
                      e?.product_group === contentSchema.value.product_group
                  );
                  let filterProduct = node.productPresetData.filter(
                    (e) => e?.product_group
                  );

                  let defaultProductName = '';
                  let defaultProductImage = '';
                  let defaultProductId = '';
                  let defaultSectionContent = '';

                  $('#product_group').attr(
                    'data-product-group',
                    contentSchema.value.product_group
                  );

                  if (filterProductGroup[0]) {
                    defaultProductName =
                      contentSchema.value.dontmiss_header_text;
                    defaultProductImage = contentSchema.value.product_url;
                    defaultProductId = contentSchema.value.dontmiss_product;
                    defaultSectionContent =
                      contentSchema.value.dontmiss_section_content;
                  } else {
                    defaultProductName = filterProduct[0]?.product_name;
                    defaultProductImage = filterProduct[0]?.product_image;
                    defaultProductId = filterProduct[0]?.p_id;
                    defaultSectionContent = filterProduct[0]?.product_content;
                  }

                  $('.trumbowyg-editor').text(defaultSectionContent);
                  for (let i = 1; i <= 5; i++) {
                    let formId = `#jsonform-${i}-elt-`;
                    $(`${formId}dontmiss_product`).val(defaultProductId);
                    $(`${formId}dontmiss_header_text`).val(defaultProductName);
                    $(
                      '.editor-image-selector-preview, ' +
                        formId +
                        'product_url'
                    ).attr('value', defaultProductImage);
                    $(`${formId}product_url`).val(defaultProductImage);
                    $(`${formId}dontmiss_url_link`).attr(
                      'value',
                      'product/' + defaultProductId
                    );
                    $(`${formId}dontmiss_url_link`).attr(
                      'value',
                      'product/' + defaultProductId
                    );
                    $(`${formId}product_url`).val(defaultProductImage);
                    $(`${formId}product_url`)
                      .parent()
                      .find('.editor-image-selector-preview img')
                      .attr('src', defaultProductImage);
                    $(`${formId}dontmiss_section_content`).text(
                      defaultSectionContent
                    );
                  }
                },
                onChange: function (evt, node) {
                  let selectedProduct =
                    node.productPresetData.find(
                      (product) => +product.p_id === +evt.target.value
                    ) || {};

                  for (let i = 1; i <= 5; i++) {
                    $(`#jsonform-${i}-elt-dontmiss_header_text`).val(
                      selectedProduct?.product_name
                    );
                    $(`#jsonform-${i}-elt-dontmiss_section_content`).val(
                      selectedProduct?.product_content
                    );
                    $(`#jsonform-${i}-elt-product_url`).val(
                      selectedProduct?.product_image
                    );
                    $(`#jsonform-${i}-elt-product_url`)
                      .parent()
                      .find('.editor-image-selector-preview img')
                      .attr('src', selectedProduct?.product_image);
                    $('.editor-image-selector-preview').attr(
                      'value',
                      selectedProduct?.product_image
                    );
                    $(`#jsonform-${i}-elt-dontmiss_url_link`).attr(
                      'value',
                      'product/' + selectedProduct?.p_id
                    );
                  }
                  $('#product_group').attr(
                    'data-product-group',
                    selectedProduct?.product_group
                  );
                  $('.trumbowyg-editor').text(selectedProduct?.product_content);
                  console.log('selectedProduct', selectedProduct);
                  console.log('Details: ', { evt, node, selectedProduct });
                },
                onClick: function (evt, node) {},
              };
              break;
            case 'en-US':
              JSONForm.fieldTypes['image'] = {
                template: `
                <div class="editor-image-selector col">
                <input type="hidden" name="<%= node.name %>" value="<%= value %>" id="<%= id %>" data-media-input-id="<%= cleanId(id) %>" />
                <div class="row">
                  <div class="col-12 ">
                    <div class="editor-image-selector-preview checkered-bg">
                      <img src="<%= value %>" class="" />
                    </div>
                  </div>
                  <div class="col-12 text-center !mt-2">
                  <!--<%= value %>-->
                    <button class="btn btn-success select-image-btn m-auto"><i class="fi fi-rr-picture align-middle mr-1"></i> Select Image</button>
                    <!--<button class="btn btn-outline-danger remove-image-btn w-100"><i class="fi fi-rr-trash align-middle mr-1"></i> Remove Image</button>-->
                  </div>
                </div>


                </div>
                `,
                fieldtemplate: true,
                inputfield: true,
                onInsert: function (evt, node) {
                  let evtTarget = evt.target ?? node[0].el;
                  let selectBtn = $(evtTarget).find(
                    '.select-image-btn, .editor-image-selector-preview'
                  );

                  selectBtn.off('click').on('click', (e) => {
                    e.preventDefault();
                    $('#media-selector-event-transport')
                      .get(0)
                      .dispatchEvent(
                        new CustomEvent('setImageInputID', {
                          detail: { imageInputID: node.id, eventData: e },
                        })
                      );
                    $(
                      '#media-selector-embed .media-selector-container .btn-select'
                    ).click();
                  });

                  let removeBtn = $(evtTarget).find('.remove-image-btn');
                  removeBtn.off('click').on('click', (e) => {
                    e.preventDefault();
                  });
                },
              };
              // Add Product Preset
              JSONForm.fieldTypes['product_preset'] = {
                //node.schemaElement.type
                fieldtemplate: true,
                inputfield: true,
                template:
                  '<select name="<%= node.name %>" id="<%= id %>"' +
                  'class=\'form-control<%= (fieldHtmlClass ? " " + fieldHtmlClass : "") %>\'' +
                  '<%= (node.schemaElement && node.schemaElement.disabled? " disabled" : "")%>' +
                  '<%= (node.schemaElement && node.schemaElement.required ? " required=\'required\'" : "") %>' +
                  '> ' +
                  `<% _.each(product_data, function(key, val)
              { if(key instanceof Object)
                {
                  if (value === key.value)
                  { %> <option selected value="<%= key.value %>"><%= key.title %></option> <%
                } else
                { %> <option value="<%= key.value %>"><%= key.title %></option> <%
              }}
              else
               { if (value === key)
                { %> <option selected value="<%= key %>"><%= key %></option> <%
              } else { %><option value="<%= key %>"><%= key %></option> <% }}}); %> ` +
                  '</select>',
                onBeforeRender: function (evt, node) {
                  const loadedProductPreset = (window as any)
                    ?.loadedProductPreset;
                  const productPresetData = loadedProductPreset.filter(
                    (e) => e.country_code === (window as any)?.country
                  );
                  node.productPresetData = productPresetData.filter(
                    (e) =>
                      e?.is_subscription === 0 &&
                      e?.display_status != 9 &&
                      e?.product_image != ''
                  );

                  evt.product_data = Object.values(node.productPresetData).map(
                    (product: any) => {
                      return {
                        value: product.p_id,
                        title: product.product_name,
                      };
                    }
                  );

                  if (node.productPresetData.length <= 0) {
                    this.hideCtaDisplay = false;
                  } else {
                    this.hideCtaDisplay = true;
                  }
                },
                onInsert: function (evt, node) {
                  if (this.hideCtaDisplay === false) {
                    // $(`#navtabscat-Content`).text('No Product Available');
                    $(`#navtabscat`).hide();
                    $(`.jsonform-error-show_product`).show();
                    $(
                      `.jsonform-error-dontmiss_product, .jsonform-error-product_url, .jsonform-error-dontmiss_header_text, .jsonform-error-dontmiss_section_content, .jsonform-error-button_text, .jsonform-error-dontmiss_url_link`
                    ).hide();
                  } else {
                    $(`#navtabscat`).show();
                    // $(`.tab-content`).text('');
                    $(
                      `.jsonform-error-show_product, .jsonform-error-dontmiss_product, .jsonform-error-product_url, .jsonform-error-dontmiss_header_text, .jsonform-error-dontmiss_section_content, .jsonform-error-button_text, .jsonform-error-dontmiss_url_link`
                    ).show();
                  }
                  let filterProductGroup = node.productPresetData.filter(
                    (e) =>
                      e?.product_group === contentSchema.value.product_group
                  );
                  let filterProduct = node.productPresetData.filter(
                    (e) => e?.product_group
                  );

                  let defaultProductName = '';
                  let defaultProductImage = '';
                  let defaultProductId = '';
                  let defaultSectionContent = '';

                  $('#product_group').attr(
                    'data-product-group',
                    contentSchema.value.product_group
                  );

                  if (filterProductGroup[0]) {
                    defaultProductName =
                      contentSchema.value.dontmiss_header_text;
                    defaultProductImage = contentSchema.value.product_url;
                    defaultProductId = contentSchema.value.dontmiss_product;
                    defaultSectionContent =
                      contentSchema.value.dontmiss_section_content;
                  } else {
                    defaultProductName = filterProduct[0]?.product_name;
                    defaultProductImage = filterProduct[0]?.product_image;
                    defaultProductId = filterProduct[0]?.p_id;
                    defaultSectionContent = filterProduct[0]?.product_content;
                  }

                  $('.trumbowyg-editor').text(defaultSectionContent);
                  for (let i = 1; i <= 5; i++) {
                    let formId = `#jsonform-${i}-elt-`;
                    $(`${formId}dontmiss_product`).val(defaultProductId);
                    $(`${formId}dontmiss_header_text`).val(defaultProductName);
                    $(
                      '.editor-image-selector-preview, ' +
                        formId +
                        'product_url'
                    ).attr('value', defaultProductImage);
                    $(`${formId}product_url`).val(defaultProductImage);
                    $(`${formId}dontmiss_url_link`).attr(
                      'value',
                      'product/' + defaultProductId
                    );
                    $(`${formId}dontmiss_url_link`).attr(
                      'value',
                      'product/' + defaultProductId
                    );
                    $(`${formId}product_url`).val(defaultProductImage);
                    $(`${formId}product_url`)
                      .parent()
                      .find('.editor-image-selector-preview img')
                      .attr('src', defaultProductImage);
                    $(`${formId}dontmiss_section_content`).text(
                      defaultSectionContent
                    );
                  }
                },
                onChange: function (evt, node) {
                  let selectedProduct =
                    node.productPresetData.find(
                      (product) => +product.p_id === +evt.target.value
                    ) || {};

                  for (let i = 1; i <= 5; i++) {
                    $(`#jsonform-${i}-elt-dontmiss_header_text`).val(
                      selectedProduct?.product_name
                    );
                    $(`#jsonform-${i}-elt-dontmiss_section_content`).val(
                      selectedProduct?.product_content
                    );
                    $(`#jsonform-${i}-elt-product_url`).val(
                      selectedProduct?.product_image
                    );
                    $(`#jsonform-${i}-elt-product_url`)
                      .parent()
                      .find('.editor-image-selector-preview img')
                      .attr('src', selectedProduct?.product_image);
                    $('.editor-image-selector-preview').attr(
                      'value',
                      selectedProduct?.product_image
                    );
                    $(`#jsonform-${i}-elt-dontmiss_url_link`).attr(
                      'value',
                      'product/' + selectedProduct?.p_id
                    );
                  }
                  $('#product_group').attr(
                    'data-product-group',
                    selectedProduct?.product_group
                  );
                  $('.trumbowyg-editor').text(selectedProduct?.product_content);
                  console.log('selectedProduct', selectedProduct);
                  console.log('Details: ', { evt, node, selectedProduct });
                },
                onClick: function (evt, node) {},
              };
              break;
            default:
              JSONForm.fieldTypes['image'] = {
                template: `
                  <div class="editor-image-selector col">
                  <input type="hidden" name="<%= node.name %>" value="<%= value %>" id="<%= id %>" data-media-input-id="<%= cleanId(id) %>" />
                  <div class="row">
                    <div class="col-12 ">
                      <div class="editor-image-selector-preview checkered-bg">
                        <img src="<%= value %>" class="" />
                      </div>
                    </div>
                    <div class="col-12 text-center !mt-2">
                    <!--<%= value %>-->
                      <button class="btn btn-success select-image-btn m-auto"><i class="fi fi-rr-picture align-middle mr-1"></i> Select Image</button>
                      <!--<button class="btn btn-outline-danger remove-image-btn w-100"><i class="fi fi-rr-trash align-middle mr-1"></i> Remove Image</button>-->
                    </div>
                  </div>


                  </div>
                  `,
                fieldtemplate: true,
                inputfield: true,
                onInsert: function (evt, node) {
                  let evtTarget = evt.target ?? node[0].el;
                  let selectBtn = $(evtTarget).find(
                    '.select-image-btn, .editor-image-selector-preview'
                  );

                  selectBtn.off('click').on('click', (e) => {
                    e.preventDefault();
                    $('#media-selector-event-transport')
                      .get(0)
                      .dispatchEvent(
                        new CustomEvent('setImageInputID', {
                          detail: { imageInputID: node.id, eventData: e },
                        })
                      );
                    $(
                      '#media-selector-embed .media-selector-container .btn-select'
                    ).click();
                  });

                  let removeBtn = $(evtTarget).find('.remove-image-btn');
                  removeBtn.off('click').on('click', (e) => {
                    e.preventDefault();
                  });
                },
              };
              // Add Product Preset
              JSONForm.fieldTypes['product_preset'] = {
                //node.schemaElement.type
                fieldtemplate: true,
                inputfield: true,
                template:
                  '<select name="<%= node.name %>" id="<%= id %>"' +
                  'class=\'form-control<%= (fieldHtmlClass ? " " + fieldHtmlClass : "") %>\'' +
                  '<%= (node.schemaElement && node.schemaElement.disabled? " disabled" : "")%>' +
                  '<%= (node.schemaElement && node.schemaElement.required ? " required=\'required\'" : "") %>' +
                  '> ' +
                  `<% _.each(product_data, function(key, val)
              { if(key instanceof Object)
                {
                  if (value === key.value)
                  { %> <option selected value="<%= key.value %>"><%= key.title %></option> <%
                } else
                { %> <option value="<%= key.value %>"><%= key.title %></option> <%
              }}
              else
               { if (value === key)
                { %> <option selected value="<%= key %>"><%= key %></option> <%
              } else { %><option value="<%= key %>"><%= key %></option> <% }}}); %> ` +
                  '</select>',
                onBeforeRender: function (evt, node) {
                  const loadedProductPreset = (window as any)
                    ?.loadedProductPreset;
                  const productPresetData = loadedProductPreset.filter(
                    (e) => e.country_code === (window as any)?.country
                  );
                  node.productPresetData = productPresetData.filter(
                    (e) =>
                      e?.is_subscription === 0 &&
                      e?.display_status != 9 &&
                      e?.product_image != ''
                  );

                  evt.product_data = Object.values(node.productPresetData).map(
                    (product: any) => {
                      return {
                        value: product.p_id,
                        title: product.product_name,
                      };
                    }
                  );

                  if (node.productPresetData.length <= 0) {
                    this.hideCtaDisplay = false;
                  } else {
                    this.hideCtaDisplay = true;
                  }
                  console.log(
                    'Selected product preset:',
                    node.productPresetData
                  );
                },
                onInsert: function (evt, node) {
                  if (this.hideCtaDisplay === false) {
                    // $(`#navtabscat-Content`).text('No Product Available');
                    $(`#navtabscat`).hide();
                    $(`.jsonform-error-show_product`).show();
                    $(
                      `.jsonform-error-dontmiss_product, .jsonform-error-product_url, .jsonform-error-dontmiss_header_text, .jsonform-error-dontmiss_section_content, .jsonform-error-button_text, .jsonform-error-dontmiss_url_link`
                    ).hide();
                  } else {
                    $(`#navtabscat`).show();
                    // $(`.tab-content`).text('');
                    $(
                      `.jsonform-error-show_product, .jsonform-error-dontmiss_product, .jsonform-error-product_url, .jsonform-error-dontmiss_header_text, .jsonform-error-dontmiss_section_content, .jsonform-error-button_text, .jsonform-error-dontmiss_url_link`
                    ).show();
                  }
                  let filterProductGroup = node.productPresetData.filter(
                    (e) =>
                      e?.product_group === contentSchema.value.product_group
                  );
                  let filterProduct = node.productPresetData.filter(
                    (e) => e?.product_group
                  );

                  let defaultProductName = '';
                  let defaultProductImage = '';
                  let defaultProductId = '';
                  let defaultSectionContent = '';

                  $('#product_group').attr(
                    'data-product-group',
                    contentSchema.value.product_group
                  );

                  if (filterProductGroup[0]) {
                    defaultProductName =
                      contentSchema.value.dontmiss_header_text;
                    defaultProductImage = contentSchema.value.product_url;
                    defaultProductId = contentSchema.value.dontmiss_product;
                    defaultSectionContent =
                      contentSchema.value.dontmiss_section_content;
                  } else {
                    defaultProductName = filterProduct[0]?.product_name;
                    defaultProductImage = filterProduct[0]?.product_image;
                    defaultProductId = filterProduct[0]?.p_id;
                    defaultSectionContent = filterProduct[0]?.product_content;
                  }

                  $('.trumbowyg-editor').text(defaultSectionContent);
                  for (let i = 1; i <= 5; i++) {
                    let formId = `#jsonform-${i}-elt-`;
                    $(`${formId}dontmiss_product`).val(defaultProductId);
                    $(`${formId}dontmiss_header_text`).val(defaultProductName);
                    $(
                      '.editor-image-selector-preview, ' +
                        formId +
                        'product_url'
                    ).attr('value', defaultProductImage);
                    $(`${formId}product_url`).val(defaultProductImage);
                    $(`${formId}dontmiss_url_link`).attr(
                      'value',
                      'product/' + defaultProductId
                    );
                    $(`${formId}dontmiss_url_link`).attr(
                      'value',
                      'product/' + defaultProductId
                    );
                    $(`${formId}product_url`).val(defaultProductImage);
                    $(`${formId}product_url`)
                      .parent()
                      .find('.editor-image-selector-preview img')
                      .attr('src', defaultProductImage);
                    $(`${formId}dontmiss_section_content`).text(
                      defaultSectionContent
                    );
                  }
                },
                onChange: function (evt, node) {
                  let selectedProduct =
                    node.productPresetData.find(
                      (product) => +product.p_id === +evt.target.value
                    ) || {};

                  for (let i = 1; i <= 5; i++) {
                    $(`#jsonform-${i}-elt-dontmiss_header_text`).val(
                      selectedProduct?.product_name
                    );
                    $(`#jsonform-${i}-elt-dontmiss_section_content`).val(
                      selectedProduct?.product_content
                    );
                    $(`#jsonform-${i}-elt-product_url`).val(
                      selectedProduct?.product_image
                    );
                    $(`#jsonform-${i}-elt-product_url`)
                      .parent()
                      .find('.editor-image-selector-preview img')
                      .attr('src', selectedProduct?.product_image);
                    $('.editor-image-selector-preview').attr(
                      'value',
                      selectedProduct?.product_image
                    );
                    $(`#jsonform-${i}-elt-dontmiss_url_link`).attr(
                      'value',
                      'product/' + selectedProduct?.p_id
                    );
                  }
                  $('#product_group').attr(
                    'data-product-group',
                    selectedProduct?.product_group
                  );
                  $('.trumbowyg-editor').text(selectedProduct?.product_content);
                  console.log('selectedProduct', selectedProduct);
                  console.log('Details: ', { evt, node, selectedProduct });
                },
                onClick: function (evt, node) {},
              };
          }

          JSONForm.fieldTypes['location'] = {
            template: `
            <div class="editor-location-selector">
            <input type="hidden" name="<%= node.name %>" value="<%= value %>" id="<%= id %>" data-location-input-id="<%= cleanId(id) %>" />
            <div class="row">
              <div class="col-12">
                <div class="location-preview-container">
                  <iframe src="https://maps.google.com/maps?hl=en&q=<%= value %>&z=15&ie=UTF8&iwloc=B&output=embed" loading="lazy" title="Location on Google Maps" allowfullscreen="allowfullscreen" class="location-preview"></iframe>
                </div>
              </div>
              <div class="col-12">
                <button class="btn btn-success my-2 select-location-btn w-100"><i class="fi fi-rr-map-marker align-middle mr-1"></i> Select Location</button>
              </div>
            </div>


            </div>
            `,
            fieldtemplate: true,
            inputfield: true,
            onInsert: function (evt, node) {
              let evtTarget = evt.target ?? node[0].el;
              let selectBtn = $(evtTarget).find(
                '.select-location-btn, .location-preview-container'
              );

              // this.coordinate cannot be accessed here. So we assign new default
              const defaultCoordinate = { lat: '3.059789', long: '101.710968' };

              // Set default coordinate if initial value is empty
              if (!node.value) {
                window['updateLocationPreview'](
                  defaultCoordinate.lat,
                  defaultCoordinate.long,
                  node.id
                );
              }

              selectBtn.off('click').on('click', (e) => {
                e.preventDefault();

                const nodeValue = $(`#${node.id}`).val();
                let currentInputLatLong = nodeValue.split(',');

                currentInputLatLong = {
                  lat: currentInputLatLong[0] || defaultCoordinate.lat,
                  long: currentInputLatLong[1] || defaultCoordinate.long,
                };

                $('#location-selector-event-transport')
                  .get(0)
                  .dispatchEvent(
                    new CustomEvent('setLocationInputID', {
                      detail: { locationInputID: node.id, eventData: e },
                    })
                  );
                $('#location-selector-event-transport')
                  .get(0)
                  .dispatchEvent(
                    new CustomEvent('setCurrentLocation', {
                      detail: {
                        currentLocation: currentInputLatLong,
                        eventData: e,
                      },
                    })
                  );
                setTimeout(() => {
                  $('#location-selector-embed .btn-open-modal').click();
                }, 10);
              });
            },
          };

          if (this.theme_id === '6') {
            JSONForm.fieldTypes['pageset'] = {
              template:
                '<fieldset class="form-group jsonform-error-<%= keydash %> <% if (elt.expandable) { %>expandable slide-deeper<% } %> <%= elt.htmlClass?elt.htmlClass:"" %>" ' +
                '<% if (id) { %> id="<%= id %>"<% } %>' +
                '>' +
                '<% if (node.title || node.legend) { %><legend role="treeitem" aria-expanded="false"><%= node.title || node.legend %></legend><% } %>' +
                '<% if (elt.expandable) { %><div class="form-group"><% } %>' +
                '<%= children %>' +
                '<% if (elt.expandable) { %></div><% } %>' +
                '</fieldset>',
              onInsert: function (evt, node) {
                $(
                  `.jsonform-error-header_template_style, .jsonform-error-footer_template_style, .jsonform-error-product_list_template_style`
                ).hide();
                if (node.title === 'Global Sections') {
                  $(node.view).hide();
                }
                if (node.el !== null) {
                  $(
                    '.expandable > div, .expandable > fieldset',
                    node.el
                  ).hide();
                  $('.expandable', node.el).removeClass('expanded');

                  //form-group expandable expanded
                }
              },
            };
          } else {
            JSONForm.fieldTypes['pageset'] = {
              template:
                '<fieldset class="form-group jsonform-error-<%= keydash %> <% if (elt.expandable) { %>expandable slide-deeper<% } %> <%= elt.htmlClass?elt.htmlClass:"" %>" ' +
                '<% if (id) { %> id="<%= id %>"<% } %>' +
                '>' +
                '<% if (node.title || node.legend) { %><legend role="treeitem" aria-expanded="false"><%= node.title || node.legend %></legend><% } %>' +
                '<% if (elt.expandable) { %><div class="form-group"><% } %>' +
                '<%= children %>' +
                '<% if (elt.expandable) { %></div><% } %>' +
                '</fieldset>',
              onInsert: function (evt, node) {
                if (node.title === 'Global Sections') {
                  $(node.view).hide();
                }
                if (node.el !== null) {
                  $(
                    '.expandable > div, .expandable > fieldset',
                    node.el
                  ).hide();
                  $('.expandable', node.el).removeClass('expanded');

                  //form-group expandable expanded
                }
              },
            };
          }

          JSONForm.fieldTypes['color'] = {
            //node.schemaElement.type
            fieldtemplate: true,
            inputfield: true,
            template: `
          <label class="row my-0">
            <div class="col">
              <div class="input-group">
                <div class="input-group-prepend">
                  <input class="color-picker" type="color" id="<%= cleanId(id) %>_col" name="<%= node.name %>_col" value="<%= escape(value) %>" rgba />
                </div>
                <input type="text" class="form-control" data-input-type="<%= cleanId(id) %>" id="<%= cleanId(id) %>" name="<%= node.name %>" value="<%= escape(value) %>" />
              </div>
            </div>
          </label>
          `,
            onInsert: function (evt, node) {
              let cleanId = window['cleanId'](node.id);
              let evtTarget = evt.target ?? node[0].el;
              let colorBtn = $(evtTarget).find(`#${cleanId}_col`);

              // Get input element
              let targetElement;

              // Get color picker element
              let targetColorpicker;

              if (typeof node.el === 'undefined') {
                targetElement = $(`#${cleanId}`).get(0);
                targetColorpicker = $(`#${cleanId}_col`);
              } else {
                targetElement = $(node.el).find(`#${cleanId}`).get(0);
                targetColorpicker = $(node.el).find(`#${cleanId}_col`);
              }

              // Add listener to color picker
              targetColorpicker.off('input').on('input', (e) => {
                targetElement.value = e.currentTarget.value;
                $(targetElement).trigger('change');
              });

              // Add listener to input
              $(targetElement)
                .off('input')
                .on('input', (e) => {
                  const colorCodeInput = e.currentTarget.value;
                  const colorCodeHex = getColorHexCode(e.currentTarget.value);

                  $(targetColorpicker).val(colorCodeHex);

                  if (colorCodeInput !== colorCodeHex) {
                    $(e.currentTarget).attr('data-color-code', colorCodeHex);
                  } else {
                    $(e.currentTarget).attr('data-color-code', null);
                  }
                });
            },
          };
          switch (this.dashboardLanguage) {
            case 'zh-TW':
              JSONForm.fieldTypes['array']['template'] =
                '<div id="<%= id %>"><ul class="_jsonform-array-ul" style="list-style-type:none;"><%= children %></ul>' +
                '<span class="_jsonform-array-buttons">' +
                '<a href="#" class="btn btn-outline-success _jsonform-array-addmore col"><i class="fi fi-rr-plus mr-2"></i> 新增<%= node.parentNode.title ?? node.title %></a>' +
                //'<a href="#" class="btn btn-outline-danger _jsonform-array-deletelast"><i class="fi fi-rr-minus-small"></i> Remove</a>' +
                '</span>' +
                '</div>';
              break;
            case 'zh-CN':
              JSONForm.fieldTypes['array']['template'] =
                '<div id="<%= id %>"><ul class="_jsonform-array-ul" style="list-style-type:none;"><%= children %></ul>' +
                '<span class="_jsonform-array-buttons">' +
                '<a href="#" class="btn btn-outline-success _jsonform-array-addmore col"><i class="fi fi-rr-plus mr-2"></i> 新增<%= node.parentNode.title ?? node.title %></a>' +
                //'<a href="#" class="btn btn-outline-danger _jsonform-array-deletelast"><i class="fi fi-rr-minus-small"></i> Remove</a>' +
                '</span>' +
                '</div>';
              break;
            case 'en-US':
              JSONForm.fieldTypes['array']['template'] =
                '<div id="<%= id %>"><ul class="_jsonform-array-ul" style="list-style-type:none;"><%= children %></ul>' +
                '<span class="_jsonform-array-buttons">' +
                '<a href="#" class="btn btn-outline-success _jsonform-array-addmore col"><i class="fi fi-rr-plus mr-2"></i> Add New <%= node.parentNode.title ?? node.title %></a>' +
                // '<a href="#" class="btn btn-outline-danger _jsonform-array-deletelast"><i class="fi fi-rr-minus-small"></i> Remove</a>' +
                '</span>' +
                '</div>';
              break;
            default:
              JSONForm.fieldTypes['array']['template'] =
                '<div id="<%= id %>"><ul class="_jsonform-array-ul" style="list-style-type:none;"><%= children %></ul>' +
                '<span class="_jsonform-array-buttons">' +
                '<a href="#" class="btn btn-outline-success _jsonform-array-addmore col"><i class="fi fi-rr-plus mr-2"></i> Add New <%= node.parentNode.title ?? node.title %></a>' +
                //'<a href="#" class="btn btn-outline-danger _jsonform-array-deletelast"><i class="fi fi-rr-minus-small"></i> Remove</a>' +
                '</span>' +
                '</div>';
              break;
          }

          JSONForm.fieldTypes['button_link'] = {
            //node.schemaElement.type
            fieldtemplate: true,
            inputfield: true,
            template: `
            <div id="<%= id %>"><ul class="_jsonform-array-ul" style="list-style-type:none;"><%= children %></ul>
              <span class="_jsonform-array-buttons">
                <button type="button" class="btn btn-outline-success _jsonform-array-addmore col" onclick="onInsertButtonClick('<%= node.schemaElement.confirm_message %>', '<%= node.schemaElement.url %>')">
                    <%= node.schemaElement.btn_label ?? node.title %>
                </button>
              </span>
            </div>
            `,
            onClick: function(evt, node) {
              onInsertButtonClick(node.schemaElement.confirm_message, node.schemaElement.url);
            }
          };

          function onInsertButtonClick(confirmMessage, url) {
            if (confirm(confirmMessage || 'Are you sure you want to proceed? Any changes that have not been saved will be lost.')) {
              window.location.href = url; // Redirect to the specified URL
            }
          }

          JSONForm.fieldTypes['array']['childTemplate'] = function (
            inner,
            enableDrag
          ) {
            let sortElement = '';
            if ($('').sortable) {
              // Insert a "draggable" icon
              // floating to the left of the main element

              // only allow drag of children if enabled
              sortElement = enableDrag
                ? '<span class="draggable line"><i class="fi fi-rr-apps-sort"></i></span>'
                : '';
            }

            return `<li data-idx="<%= node.childPos %>" class="mb-4">
            ${sortElement}
            <div class="float-right"><span class="_jsonform-array-buttons">
            <a href="#" class="btn btn-outline-danger _jsonform-array-deletecurrent"><i class="fi fi-rr-trash"></i></a>
            </span></div>
            ${inner}
            </li>`;
          };

          // Remove form and readd to prevent duplicate event listener. (We do not have access to .off() the event listener)
          $('#content-editor-form').remove();
          $('#form-wrapper').append(
            `<form class="content-editor-form" id="content-editor-form" name="content-editor-form" ></form>`
          );

          JSONForm.fieldTypes['product_selection'] = {
            //node.schemaElement.type
            fieldtemplate: true,
            inputfield: true,
            template:
              '<select name="<%= node.name %>" id="<%= id %>"' +
              'class=\'form-control<%= (fieldHtmlClass ? " " + fieldHtmlClass : "") %>\'' +
              '<%= (node.schemaElement && node.schemaElement.disabled? " disabled" : "")%>' +
              '<%= (node.schemaElement && node.schemaElement.required ? " required=\'required\'" : "") %>' +
              '> ' +
              `<% _.each(product_data, function(key, val)
            { if(key instanceof Object)
              {
                if (value === key.value)
                { %> <option selected value="<%= key.value %>"><%= key.title %></option> <%
              } else
              { %> <option value="<%= key.value %>"><%= key.title %></option> <%
            }}
            else
             { if (value === key)
              { %> <option selected value="<%= key %>"><%= key %></option> <%
            } else { %><option value="<%= key %>"><%= key %></option> <% }}}); %> ` +
              '</select>',
            onBeforeRender: function (evt, node) {
              const loadedProductPreset = (window as any)
                ?.loadedProductPreset;
              const productPresetData = loadedProductPreset.filter(
                (e) => e.country_code === (window as any)?.country
              );
              node.productPresetData = productPresetData.filter(
                (e) =>
                  e?.is_subscription === 0 &&
                  e?.display_status !== 9
              );

              evt.product_data = Object.values(node.productPresetData).map(
                (product: any) => {
                  return {
                    value: product.p_id,
                    title: product.product_name,
                  };
                }
              );
            }
          };

          // Add generated form element
          const jsonForm = $(formSelector).jsonForm(contentSchema);

          const assignEventListeners = () => {
            // jQuery > Angular event data transport
            $(formSelector)
              .off('change keyup')
              .on('change keyup', (e) => {
                console.log('ON CHANGE', { e });
                $('#content-editor-event-transport')
                  .get(0)
                  .dispatchEvent(new Event('formUpdate'));
              });

            $(formSelector)
              .find('textarea')
              .trumbowyg({
                btns: [['viewHTML'], ['formatting'], ['strong', 'em']],
                autogrow: false,
              });

            // Send focus event
            $(formSelector)
              .find('input,textarea')
              .off('focus')
              .on('focus', (e) => {
                const inputId = (e.currentTarget?.id ?? '').split('-elt-')[1];
                $('#content-editor-event-transport')
                  .get(0)
                  .dispatchEvent(
                    new CustomEvent('formFocus', {
                      detail: { id: inputId, eventData: e },
                    })
                  );
              });

            // Send blur event
            $(formSelector)
              .find('input,textarea')
              .off('blur')
              .on('blur', (e) => {
                const inputId = (e.currentTarget?.id ?? '').split('-elt-')[1];
                $('#content-editor-event-transport')
                  .get(0)
                  .dispatchEvent(
                    new CustomEvent('formBlur', {
                      detail: { id: inputId, eventData: e },
                    })
                  );
              });

            // Send blur event
            $('._jsonform-array-addmore')
              .off('click.reassign')
              .on('click.reassign', (e) => {
                // Adding new item for array will reassign all event listeners
                assignEventListeners();
              });

            setTimeout(() => this.handleFormUpdate(null), 1000);
          };

          // First time init
          assignEventListeners();
        } catch (err) {
          console.log({ err });
        }
      }, 10);
    }
  }

  sendPostMessageToStorefront(payload) {
    $('#editor-preview').get(0).contentWindow.postMessage(payload, '*');
  }

  formatContentData(formatData = false) {
    // Content data for saving require some sanatization & formating.
    // Such as converting color "red" to "#ff0000" before calling save API
    if (formatData) {
      $('[data-color-code]').each((i, colorInput) => {
        let colorCode = $(colorInput).attr('data-color-code');
        $(colorInput).val(colorCode).attr('data-color-code', null);
      });
    }

    this.currentContentData = $('#content-editor-form').jsonFormValue();

    // jsonFormValue() only returns fields the form actually rendered, and the
    // Store Logo fields are no longer among them. Carry the saved values across
    // so the preview keeps showing the real logo on every edit, and so the
    // blank-missing-keys pass in handleSaveChanges can't publish them as ''.
    STORE_LOGO_KEYS.forEach((key) => {
      if (this.contentData && key in this.contentData) {
        this.currentContentData[key] = this.contentData[key];
      }
    });

    return this.currentContentData;
  }

  handleOpenAIAssistantModal(event?) {
    console.log('event', event);
    if (event.detail?.aiType === 'GENERATE_CTA_DESC') {
      let productId = $('.jsonform-error-dontmiss_url_link input')?.val();
      productId = productId?.split('/')?.[1] ?? '1001';
      console.log('CTA productId', productId);
      event.detail.aiparameters.product_id = productId;
    }
    event.detail.aiparameters.language_code = `${this.selectedLanguage?.language_name} (${this.selectedLanguage?.language_code})`;

    this.utils.aiContentGenerator.show(
      event.detail?.aiType,
      event.detail?.aiparameters,
      (response) => {
        $(`#${event.detail?.nodeId}`).trumbowyg('html', response);
        this.handleFormUpdate();
      }
    );
  }

  handleSetImageInputID(event?) {
    this.lastImageURL = '';
    this.lastMediaID = '';
    this.lastImageInputID = event.detail.imageInputID;
  }

  handleMediaChanged(event?) {
    let cleanId = window['cleanId'](this.lastImageInputID);
    const mediaInputSelector = `[data-media-input-id="${cleanId}"]`;

    $(mediaInputSelector).val(event.url);
    $(mediaInputSelector).trigger('change');
    $(mediaInputSelector)
      .closest('.editor-image-selector')
      .find('.editor-image-selector-preview img')
      .attr('src', event.url);
  }

  handleSetCurrentLocation(event?) {
    console.log(['handleSetCurrentLocation', event.detail.currentLocation]);
    this.coordinate = event.detail.currentLocation;
  }

  handleSetLocationInputID(event?) {
    this.coordinateStr = '';
    this.lastCoordinateInputID = event.detail.locationInputID;
  }

  handleCoordinateChanged(event?) {
    console.log('handleCoordinateChanged', this.coordinate);
    window['updateLocationPreview'](
      event.lat,
      event.long,
      this.lastCoordinateInputID,
      true
    );
  }

  handleFormUpdate(event?) {
    this.formatContentData(false);

    // Send data to storefront preview window
    this.sendPostMessageToStorefront({
      customContent: this.currentContentData,
    });
  }

  handleFormFocus(event?) {
    // Send focus event to storefront preview window
    this.sendPostMessageToStorefront({ focusedInput: event.detail?.id });
  }

  handleFormBlur(event?) {
    // Send blur event to storefront preview window
    this.sendPostMessageToStorefront({ blurredInput: event.detail?.id });
  }

  handleSaveChanges(event) {
    // Format content
    this.formatContentData(true);

    // let { currentContentData } = this;
    for (const key in this.contentData) {
      if (!(key in this.currentContentData)) {
        this.currentContentData[key] = '';
      }
    }
    let current = { ...this.contentData, ...this.currentContentData };
    let designMerge = { ...this.design, ...current };
    let currentContentData = { ...designMerge };
    let designContent = {};
    for (const key in this.design) {
      if (this.design.hasOwnProperty(key) && designMerge.hasOwnProperty(key)) {
        designContent[key] = designMerge[key];
      }
    }

    for (const key in this.design) {
      if (this.design.hasOwnProperty(key)) {
        delete currentContentData[key];
      }
    }

    currentContentData.product_group =
      $('#product_group').attr('data-product-group');

    const payload = {
      theme_id: this.theme_id,
      store_id: this.store_id,
      design: designContent,
      content: currentContentData,
      language_code: this.selectedLanguage.language_code,
    };

    this.svc.updateCustomContent(payload, this.token).subscribe((res: any) => {
      console.log(res);
      if (res.status === 'success') {
        console.log('Update success.. SHOW TOAST');
        this.utils.notify.showSuccess(
          this.utils.translate('Updated Successfully!')
        );
      }
    });
  }

  get customContentDataIsChanged() {
    const currentContentData = JSON.stringify(
      this.quickSort(this.currentContentData)
    );
    return currentContentData === this.lastSavedContentData;
  }

  resizePreviewContainer() {
    let parentWidth = document.documentElement.scrollWidth;
    let iframeWidth = $('#editor-preview').width();
    let previewWidth = $('.store-preview-column').width();
    let scale = previewWidth / iframeWidth;
    scale = scale > 1 ? 1 : scale;

    try {
      $('.store-preview').css('transform', `scale(${scale})`);

      if (scale === 1) {
        $('#editor-preview').css('width', `100%`);
      } else {
        $('#editor-preview').css('width', null);
      }

      const containerHeight = $('.store-preview-column').height();
      const iframeHeight = $('#editor-preview').height();

      let finalHeight = containerHeight * (1 / scale);

      $('#editor-preview').css('height', `${finalHeight}px`);
    } catch (err) {
      console.warn('Issue', err);
    }
  }

  // Dragging the divider between the options panel and the preview. Bound in
  // the template: the divider sits inside an *ngIf, so looking it up from
  // ngOnInit (as this used to) found nothing and threw before the handler
  // was attached. Widths are pixels; the old code wrote pixel offsets as
  // percentages, so a 400px panel became width: 450%.
  startPanelResize(downEvent: MouseEvent) {
    const panel = document.getElementById('first');
    const wrapper = panel?.parentElement;
    if (!panel || !wrapper || downEvent.button !== 0) return;
    downEvent.preventDefault(); // no text selection while dragging

    const startX = downEvent.clientX;
    const startWidth = panel.offsetWidth;
    // The panel's own min-width (CSS) is the floor; leave the preview room.
    const maxWidth = wrapper.offsetWidth - 360;
    const container = $('.content-editor-container');
    let frame = 0;

    container.addClass('is-resizing');

    const onMove = (e: MouseEvent) => {
      const width = Math.min(startWidth + e.clientX - startX, maxWidth);
      panel.style.width = `${width}px`;
      if (!frame) {
        frame = requestAnimationFrame(() => {
          frame = 0;
          this.resizePreviewContainer();
        });
      }
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      cancelAnimationFrame(frame);
      container.removeClass('is-resizing');
      this.resizePreviewContainer();
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }

  toggleFullpage() {
    this.fullpageMode = !this.fullpageMode;

    if (this.fullpageMode) {
      $('html').addClass('fullpage-preview-mode');
      $('html').removeClass('normal-preview-mode');
    } else {
      $('html').removeClass('fullpage-preview-mode');
      $('html').addClass('normal-preview-mode');
    }

    setTimeout(this.resizePreviewContainer, 100);
  }

  injectPreviewAutoResize() {
    // Add listener to preview container
    $(window).on('resize', (e) => {
      this.resizePreviewContainer();
    });

    // Initialize preview container size
    [500, 1000, 1500, 2000, 2500].forEach((time) => {
      setTimeout(this.resizePreviewContainer, time);
    });
  }

  changeLanguage(language) {
    console.log('Changing language: ', language);
    this.selectedLanguage = language;
    this.loadContentData();
  }

  fetchCountry() {
    this.svc.getCountry(this.token).subscribe((res) => {
      res['data'].forEach((item) => {
        if (item['country_name'] == 'Malaysia-Brunei') {
          item['country_name'] = 'Brunei';
        }
      });
      let defaultCountry = res['data'].find((index) => {
        if (parseInt(index['default']) === 1) {
          return index;
        }
      });

      this.country = defaultCountry.country_code;
      window['country'] = this.country;
    });
  }

  ngOnInit(): void {
    const token = localStorage.getItem('bearer');
    const store_id = JSON.parse(localStorage.getItem('store_id'));
    const dashboardLangauge = localStorage.getItem('dashboard-lang');

    $.trumbowyg.svgPath = '/library/trumbowyg-dist/ui/icons.svg';

    let theme_id;

    this.token = token;
    this.store_id = store_id;
    this.dashboardLanguage = dashboardLangauge;

    this.fetchCountry();
    this.fetchProductArrangement();

    // Load store basic data
    this.storeInfo$.subscribe((res) => {
        this.store_url = res.store_url;
        theme_id = res.theme_id;
        this.theme_id = theme_id;

        let store_url = this.store_url;

        // Sanatize store_url
        this.safe_store_url =
          this.sanitizer.bypassSecurityTrustResourceUrl(store_url);

        // Load theme custom content form structure schema
        this.svc.getSchema(theme_id, token).subscribe((res: any) => {
          const schemaList = res.data ?? [];
          let customContentJson = {};
          console.log('schemaList', schemaList);

          try {
            customContentJson = JSON.parse(schemaList.schema);
            this.design = JSON.parse(schemaList.preset_design_data);
            this.presetContent = JSON.parse(schemaList.preset_content_data);
            customContentJson = this.removeStoreLogoFields(customContentJson);
            customContentJson = this.translateSchema(customContentJson);
            this.contentSchema = customContentJson;
            this.tryAttachValueToSchema();
          } catch {
            console.warn('SCHEMA_INVALID_JSON', { schemaList });
          }
        });

      // Load languages list
      this.svc.getStoreLanguage(token, this.store_id).subscribe((res) => {
        const allLanguages = res['data'];

        const enabledLanguages = allLanguages.filter((lang) => +lang.status);
        const defaultLanguage =
          allLanguages.find((lang) => +lang.default) ?? allLanguages[0];

        this.languages = enabledLanguages;
        this.selectedLanguage = defaultLanguage;

        this.loadContentData();
      });

      // Initiate preview section width resize listener
      this.injectPreviewAutoResize();
    });

    // Attach dealer info to window
    this.dataService.dealerInfo$.subscribe(data => {
      window['dealerInfo'] = data;
    });
  }

  getSafeUrl(url: string): SafeResourceUrl {
    url = this.safe_store_url;
    return this.sanitizer.bypassSecurityTrustResourceUrl(url);
  }

  loadContentData() {
    const { store_id, selectedLanguage, theme_id, token } = this;

    this.contentDataReady = false;

    (window as any).country = this.country;
    // Load custom content data
    this.svc
      .getCustomContent(
        store_id,
        selectedLanguage.language_code,
        theme_id,
        token
      )
      .subscribe((res: any) => {
        console.log('res.datares.data', res.data);
        let contentDataStr = res.data?.content ?? '{}';

        console.log({ contentDataStr });
        const contentData = JSON.parse(contentDataStr);
        let presetContentMerge = { ...this.presetContent, ...contentData };
        this.contentData = presetContentMerge;
        this.currentContentData = contentData;
        console.log('LOADED contentData', { contentData });

        this.lastSavedContentData = JSON.stringify(this.quickSort(contentData));

        this.contentDataReady = true;
      });

    this.svc
      .getProductLists(token, this.store_id, selectedLanguage.language_code)
      .subscribe((res) => {
        let productList = res['data'];
        this.loadedProductPreset = productList;
        // (window as any).loadedProductPreset = productList;
        this.tryAttachValueToSchema();
        this.generateArrangementList(true);
      });
  }

  fetchProductArrangement() {
    this.svc.getProductCustomArrangement(this.token).subscribe(
      (res) => {
        this.customArrangement = res?.['data'];
        console.log();
        this.sortByArrangementList();
        this.customArrangementReady = true;
      },
      (error) => {
        console.log(error);
      }
    );
  }

  generateArrangementList(doSort = false) {
    let productArrangement = Object.entries(this.loadedProductPreset).map(
      (product: any) => +product[1].p_id
    );

    this.productArrangement = productArrangement;
    if (doSort) this.sortByArrangementList();
  }

  sortByArrangementList() {
    this.customArrangement;
    this.productArrangement;

    console.log('Trying to sort sortByArrangementList');

    if (this.customArrangementReady && this.productArrangement?.length) {
      // Arrangement ready
      let countryCode = this.country;
      let newArrangement = [...(this.customArrangement[countryCode] ?? [])];
      this.productArrangement.forEach((p_id) => {
        if (!newArrangement.includes(p_id)) {
          newArrangement.push(p_id);
        }
      });
      this.productArrangement = newArrangement;

      let arrangedProducts = [];
      console.log('Sort by arrangement');
      newArrangement.forEach((p_id) => {
        let productFound = this.loadedProductPreset.find(
          (product) => +product.p_id === +p_id
        );

        // Only insert non-empty
        if (productFound) arrangedProducts.push(productFound);
      });

      // Replace displayedProducts with arrangedProducts
      this.loadedProductPreset = arrangedProducts;
      (window as any).loadedProductPreset = this.loadedProductPreset;
    }
  }

  translateSchema(schema) {
    let translateStrings = {};

    const translateTitle = (objs, fromKey = '') => {
      // If object/array, we will interate deeper
      if (typeof objs === 'object') {
        let newObjs;
        let objIsArray = Array.isArray(objs);
        let keys = Object.keys(objs);

        if (objIsArray) {
          newObjs = [];
        } else {
          newObjs = {};
        }

        for (let key of keys) {
          newObjs = translateTitle(objs[key], key);
          objs[key] = newObjs;
        }
      } else {
        if (
          ['title', 'inlinetitle', 'description'].includes(
            fromKey.toLowerCase()
          )
        ) {
          objs = this.utils.translate(objs);
          translateStrings[objs] = objs;
        }
      }

      return objs;
    };
    return translateTitle(schema);
  }

  viewStore() {
    let store_url = localStorage.getItem('store_url');
    if (store_url != null) {
      window.open(store_url, '_blank');
    } else {
      this.utils.notify.showWarning(
        this.utils.translate('Something went wrong! please contact support.'),
        this.utils.translate('Uh oh!')
      );
    }
  }
}
